import { useEffect, useState } from "react";
import WordCloud from "react-d3-cloud";
import {
  Typography,
  ListItem,
  ListItemText,
  List,
  Grid,
  Pagination,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  Box,
} from "@mui/material";
import $ from "jquery";
import { ucWords } from "../util/StringUtil";
import { getKeywordSource } from "../api/specialization/KeywordSpecializationAPI";
import React from "react";
import ContentListPopup from "./popup/ContentListPopup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { errorNoti } from "../util/SnackbarMessage";
import { Chart, registerables } from "chart.js";
import { DirectionalNumber } from "./DirectionalNumber";
import { isLogin } from "../api/authentication/AuthenticationAPI";
Chart.register(...registerables);

type WordCloudProps = {
  words: any[];
  time: number;
  oldRanks?: any[];
  noTable?: boolean;
  sentiment?: any;
};

function compare(a: any, b: any) {
  if (a.points < b.points) {
    return -1;
  }
  if (a.points == b.points) {
    return 0;
  }
  return 1;
}

function WordCloudWrapper(props: WordCloudProps) {
  const words = props.words;

  const [chosenWord, setChosenWord] = useState({ keyword: "" });

  const [popup, setPopup] = useState(false);

  const [sources, setSources] = useState([]);

  const { t } = useTranslation(["common", "error", "keyword"]);
  const { enqueueSnackbar } = useSnackbar();
  const [wordPage, setWordPage] = useState(1);

  const onClosePopup = () => {
    setPopup(false);
    setSources([]);
  };
  const onDataPageChange = (event: any, page: any) => setWordPage(page);

  useEffect(() => {
    if (chosenWord.keyword.length > 0) {
      getKeywordSource(chosenWord.keyword)
        .then((res) => setSources(res))
        .catch((err) => {
          var noti = errorNoti(err);
          enqueueSnackbar(t("error:" + noti.message), noti.option);
        });
    }
  }, [chosenWord]);

  var maxPts = Math.max(
    ...words.map(function (word) {
      return word["points"];
    })
  );

  var results = words.map(function (word) {
    var minSize = 1500;
    return {
      text: ucWords(word["token"]),
      value: Math.max((word["points"] / maxPts) * 6000, minSize),
      points: word["points"],
      index: 0,
      oldRank: [{ rank: 0, keyword: "" }],
    };
  });

  results.sort(compare);

  for (var i = 0; i < results.length; i++) {
    results[i]["index"] = i;
  }

  results = results.map(function (word) {
    var value = 600 + Math.min(props.time * 10, 200);
    value = value * ((word.index + 2) / 12);
    var oldRank = props.oldRanks
      ? props.oldRanks.filter(
          (rank) => rank["keyword"] == word.text.toLowerCase()
        )
      : [{ rank: word.index, keyword: word.text }];
    return {
      text: word.text,
      value: value,
      index: word.index,
      points: word["points"],
      oldRank: oldRank,
    };
  });

  var listItems = results
    .slice(
      Math.max(0, Math.min(5, words.length / 10) - wordPage) * 10,
      Math.max(1, Math.min(5, words.length / 10) - wordPage + 1) * 10
    )
    .map(function (word) {
      var rankChanged = words.length - word.index;
      var rank = results.length - word.index;
      var iconColor = "gray";
      if (rank == 1) {
        iconColor = "gold";
      }
      if (rank == 2) {
        iconColor = "silver";
      }
      if (rank == 3) {
        iconColor = "#cd7f32";
      }
      if (word.oldRank.length > 0 && word.oldRank[0].rank != 0) {
        rankChanged = word.oldRank[0].rank - rankChanged;
      } else {
        rankChanged = Infinity;
      }
      return (
        <ListItem
          onClick={() => {
            if (isLogin()) {
              setChosenWord({ keyword: word.text });
              setPopup(!popup);
            }
          }}
          className="slide-from-right word-cloud-keyword-list-item"
          style={{
            animation: "fadeIn 1s",
            paddingTop: "0px",
            paddingBottom: "0px",
            paddingLeft: "6px",
            paddingRight: "6px",
          }}
        >
          <ListItemAvatar
            sx={{
              minWidth: "35px !important",
            }}
          >
            <Avatar
              style={{
                width: "28px",
                height: "28px",
                color: "info",
                backgroundColor: iconColor,
              }}
            >
              {results.length - word.index}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            style={{
              width: "55%",
              maxWidth: "55%",
              marginBottom: "4px",
            }}
            primary={word.text.replace(
              /(^\w{1})|(\s{1}\w{1})/g,
              function (letter) {
                return letter.toUpperCase();
              }
            )}
            primaryTypographyProps={{
              fontSize: "13px",
              fontWeight: () => {
                if (results.length - word.index < 2) {
                  return 700;
                }
                if (results.length - word.index < 5) {
                  return 600;
                }
                return 400;
              },
            }}
            secondary={
              t("keyword:hot_index") +
              ": " +
              Math.ceil((word.points / maxPts) * 100)
            }
            secondaryTypographyProps={{ fontSize: "12px" }}
          ></ListItemText>
          {word.points ? (
            <Box sx={{ width: "20%", marginRight: "2px" }}>
              <LinearProgress
                variant="determinate"
                color="info"
                value={(word.points / maxPts) * 100}
              />
            </Box>
          ) : (
            ""
          )}

          {props.oldRanks ? (
            <DirectionalNumber
              number={rankChanged}
              infinity={rankChanged == Infinity}
            ></DirectionalNumber>
          ) : (
            ""
          )}
        </ListItem>
      );
    });

  listItems = listItems.reverse();
  listItems = listItems.slice(0, 10);
  return (
    <Grid container paddingLeft={"2px"} paddingRight={"2px"}>
      <Grid item xs={0}></Grid>
      <Grid
        item
        paddingTop={1}
        paddingBottom={4}
        xs={props.noTable ? 12 : 7.5}
        id={"wordcloud-box-wrapper"}
        className={
          props.noTable ? "white" : "grid-border white slide-from-left"
        }
      >
        <WordCloud
          padding={5}
          rotate={0}
          random={() => 1}
          data={results}
          spiral={"rectangular"}
          onWordMouseOver={(event, d) => {
            var elements = $("#wordcloud-box-wrapper text");
            for (var i = 0; i < elements.length; i++) {
              if (elements[i].textContent == d.text) {
                var currentTransform = $(elements[i]).attr("transform");
                $(elements[i]).attr(
                  "transform",
                  currentTransform + "scale(1.1)"
                );
                $(elements[i]).css("transition-duration", "0.2s");
              }
            }
          }}
          onWordMouseOut={(event, d) => {
            var elements = $("#wordcloud-box-wrapper text");
            for (var i = 0; i < elements.length; i++) {
              if (elements[i].textContent == d.text) {
                var currentTransform = $(elements[i])
                  .attr("transform")
                  .split("scale")[0];
                $(elements[i]).attr("transform", currentTransform);
              }
            }
          }}
          onWordClick={(event, d) => {
            if (isLogin()) {
              setChosenWord({ keyword: d.text });
              setPopup(!popup);
            }
          }}
          font="Lexend Deca"
          fontWeight={(word) => {
            if (word.value < 2000) {
              return 100;
            }
            if (word.value < 3000) {
              return 500;
            }
            return 600;
          }}
        />
        {isLogin() ? (
          <ContentListPopup
            popup={popup}
            onClosePopup={onClosePopup}
            contents={sources}
            title={"Các bài báo gần đây về " + chosenWord.keyword}
          />
        ) : (
          ""
        )}
      </Grid>
      {props.noTable ? (
        ""
      ) : (
        <>
          <Grid item xs={0.5}></Grid>
          <Grid
            item
            xs={4}
            alignItems="center"
            justifyContent="center"
            className="grid-border white slide-from-left"
            sx={{ padding: "0 12px" }}
          >
            {words.length > 0 ? (
              <List className="">
                <Typography
                  variant="h6"
                  align="left"
                  marginLeft={"6px"}
                  fontWeight={500}
                  marginBottom={"5px"}
                >
                  {t("keyword:keyword_list")}
                </Typography>
                <Typography
                  variant="body1"
                  align="left"
                  marginLeft={"6px"}
                  fontWeight={500}
                  marginBottom={"10px"}
                >
                  {t("keyword:ranking_changes") +
                    " 24h " +
                    t("common:recent_hours")}
                </Typography>
                {listItems}
              </List>
            ) : (
              ""
            )}
            {words.length > 0 ? (
              <Pagination
                id="keywords-pagination"
                className=""
                count={Math.max(Math.ceil(words.length / 10), 1)}
                page={wordPage}
                onChange={onDataPageChange}
                color="primary"
                variant="text"
                size="small"
                sx={{
                  marginTop: "10px !important",
                  marginBottom: "10px !important",
                }}
              />
            ) : (
              ""
            )}
          </Grid>
          <Grid item xs={0}></Grid>
        </>
      )}
    </Grid>
  );
}

export default WordCloudWrapper;
